import { sx } from '../lib/sx'
import { Hov } from '../lib/Hov'
import type { Vals } from '../demo/vals'

/**
 * The OS share sheet, standing in for the system UI the invite link hands off
 * to. Deliberately not a NordVPN surface — it is the platform's, and the demo
 * only needs it to be recognisable enough that the handoff reads as real.
 */
export function ShareSheet({ v }: { v: Vals }) {
  return (
    <div style={sx('position:absolute;inset:0;z-index:12;display:flex;flex-direction:column;justify-content:flex-end')}>
      <button
        onClick={v.closeShareSheet}
        aria-label="Dismiss share sheet"
        style={sx(
          'position:absolute;inset:0;border:none;padding:0;cursor:pointer;' +
            'background:rgba(0,0,0,.45);backdrop-filter:blur(2px)'
        )}
      />

      <div
        style={sx(
          'position:relative;margin:0 8px 10px;border-radius:38px;background:#1c1c1e;' +
            'padding:16px;display:flex;flex-direction:column;gap:14px;' +
            'box-shadow:0 -8px 40px rgba(0,0,0,.6);animation:sheet-up 260ms var(--ease-out)'
        )}
      >
        <div style={sx('display:flex;align-items:center;gap:12px')}>
          <span
            style={sx(
              'width:52px;height:52px;flex:none;border-radius:12px;background:var(--bg-accent);' +
                'display:flex;align-items:center;justify-content:center'
            )}
          >
            <span
              style={sx(
                'width:26px;height:26px;background:#fff;' +
                  '-webkit-mask:url(/assets/icons/refer.svg) center/contain no-repeat;' +
                  'mask:url(/assets/icons/refer.svg) center/contain no-repeat'
              )}
            />
          </span>
          <span style={sx('flex:1;display:flex;flex-direction:column;gap:2px;min-width:0')}>
            <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-bold);color:#fff')}>
              {v.shareSheetTitle}
            </span>
            <span style={sx('font-size:var(--fs-xs);color:rgba(255,255,255,.55)')}>
              nordvpn.com
            </span>
          </span>
          <Hov
            as="button"
            onClick={v.closeShareSheet}
            aria-label="Close"
            style={sx(
              'width:30px;height:30px;flex:none;padding:0;border:none;cursor:pointer;' +
                'border-radius:var(--radius-full);background:rgba(255,255,255,.12);' +
                'display:flex;align-items:center;justify-content:center'
            )}
            hoverStyle={sx('background:rgba(255,255,255,.2)')}
          >
            <span
              style={sx(
                'width:13px;height:13px;background:rgba(255,255,255,.75);' +
                  '-webkit-mask:url(/assets/icons/close.svg) center/contain no-repeat;' +
                  'mask:url(/assets/icons/close.svg) center/contain no-repeat'
              )}
            />
          </Hov>
        </div>

        <Divider />

        <div style={sx('display:flex;gap:18px;overflow:hidden')}>
          {v.shareContacts.map((c) => (
            <Hov
              key={c.key}
              as={c.live ? 'button' : 'span'}
              onClick={c.live ? () => v.shareInviteTo(c.name) : undefined}
              style={sx(
                'display:flex;flex-direction:column;align-items:center;gap:6px;width:62px;flex:none;' +
                  'padding:0;border:none;background:transparent;font-family:inherit' +
                  (c.live ? ';cursor:pointer' : '')
              )}
              hoverStyle={c.live ? sx('opacity:.75') : undefined}
            >
              <span
                style={sx(
                  `width:54px;height:54px;border-radius:var(--radius-full);background:${c.tint};` +
                    `color:#fff;display:flex;align-items:center;justify-content:center;` +
                    `font-size:var(--fs-lg);font-weight:var(--fw-medium)`
                )}
              >
                {c.initials}
              </span>
              <span
                style={sx(
                  'font-size:var(--fs-2xs);color:rgba(255,255,255,.75);' +
                    'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%'
                )}
              >
                {c.name}
              </span>
            </Hov>
          ))}
        </div>

        <Divider />

        <div style={sx('display:flex;gap:18px')}>
          <ShareApp icon="wifi" label="AirDrop" tint="#0a84ff" />
          <ShareApp icon="message" label="Messages" tint="#32d74b" />
          <ShareApp icon="email" label="Mail" tint="#0a84ff" />
          <ShareApp icon="copy" label="Copy" tint="rgba(255,255,255,.14)" muted />
        </div>
      </div>
    </div>
  )
}

function Divider() {
  return <span style={sx('height:1px;background:rgba(255,255,255,.1)')} />
}

function ShareApp({
  icon,
  label,
  tint,
  muted,
}: {
  icon: string
  label: string
  tint: string
  muted?: boolean
}) {
  return (
    <span style={sx('display:flex;flex-direction:column;align-items:center;gap:6px;width:62px;flex:none')}>
      <span
        style={sx(
          `width:54px;height:54px;border-radius:14px;background:${tint};` +
            `display:flex;align-items:center;justify-content:center`
        )}
      >
        <span
          style={sx(
            `width:26px;height:26px;background:${muted ? 'rgba(255,255,255,.85)' : '#fff'};` +
              `-webkit-mask:url(/assets/icons/${icon}.svg) center/contain no-repeat;` +
              `mask:url(/assets/icons/${icon}.svg) center/contain no-repeat`
          )}
        />
      </span>
      <span style={sx('font-size:var(--fs-2xs);color:rgba(255,255,255,.75);white-space:nowrap')}>
        {label}
      </span>
    </span>
  )
}
